<template>
  <div>
    <v-dialog v-model="addModuleDialog" persistent :width="width">
      <v-card>
        <v-toolbar color="#EEF2F6">
          <v-card-title>
            <i class="fal fa-books mr-2"></i>
            <span class="headline">Add Module</span>
          </v-card-title>
          <v-spacer></v-spacer>
          <v-tooltip top>
            <template v-slot:activator="{ on, attrs }">
              <button
                type="button"
                class="btn btn-light btn-elevate-hover btn-circle btn-icon btn-sm mr-2"
                v-bind="attrs"
                v-on="on"
                readonly=""
                @click="toggleHelperDialog"
              >
                <i class="fas fa-question" />
              </button>
            </template>
            <span>More information</span>
          </v-tooltip>
          <button
            type="button"
            class="btn btn-light btn-elevate-hover btn-circle btn-icon btn-sm"
            :disabled="isLoading"
            @click="close"
          >
            <i class="fal fa-times"></i>
          </button>
        </v-toolbar>

        <v-card-text>
          <v-container>
            <v-row>
              <v-col cols="12" sm="12" md="6">
                <v-text-field
                  v-model="module.name"
                  label="Module Name"
                  name="name"
                  v-validate="'required'"
                  :disabled="isLoading"
                  :loading="isLoading"
                ></v-text-field>
                <span
                  class="kt-font-danger validate-error"
                  v-show="errors.has('name')"
                  >{{ errors.first('name') }}</span
                >
              </v-col>
              <v-col cols="12" sm="12" md="6">
                <v-row>
                  <v-text-field
                    name="fileUploadOrLink"
                    v-model="module.image"
                    label="Thumbnail"
                    @click="toggleUploadDialog"
                  >
                  </v-text-field>
                </v-row>
              </v-col>
              <v-col cols="12" sm="12" md="12">
                <div>
                  Related Links
                  <v-tooltip top>
                    <template #activator="{ on }">
                      <v-icon x-small v-on="on"> fas fa-info-circle </v-icon>
                    </template>
                    <v-img
                      src="https://webinarinc-v2-development.s3.us-west-2.amazonaws.com/menu-items-external-links.PNG"
                    />
                    <span
                      ><i
                        ><small
                          >Shows on the Related Links list on the module
                          page</small
                        ></i
                      ></span
                    >
                  </v-tooltip>
                </div>
                <draggable
                  :list="links"
                  :disabled="isLoading"
                  @start="dragging = true"
                  @end="dragging = false"
                  :options="{ animation: 200, group: 'status' }"
                  :element="'div'"
                  v-model="links"
                >
                  <v-row
                    v-for="(link, index) in links"
                    :key="link.key"
                    align="center"
                    justify="center"
                  >
                    <v-col cols="12" sm="12" md="1">
                      <v-icon> fal fa-arrows </v-icon>
                    </v-col>
                    <v-col cols="12" sm="12" md="5">
                      <v-text-field
                        v-model="link.name"
                        label="Name that will display on the list"
                        :disabled="isLoading"
                        :loading="isLoading"
                      />
                    </v-col>
                    <v-col cols="12" sm="12" md="5">
                      <v-text-field
                        v-model="link.url"
                        label="Source URL"
                        :disabled="isLoading"
                        :loading="isLoading"
                        @click="toggleLinkDialog(index)"
                      />
                    </v-col>
                    <v-col cols="12" sm="12" md="1">
                      <v-spacer />
                      <v-btn
                        @click="addLink"
                        small
                        dark
                        text
                        :disabled="isLoading"
                        :loading="isLoading"
                        color="success darken-3"
                      >
                        <v-icon small>fas fa-plus-circle</v-icon>
                      </v-btn>
                      <v-btn
                        @click="removeLink(index)"
                        v-if="links.length > 1"
                        small
                        dark
                        text
                        :disabled="isLoading"
                        :loading="isLoading"
                        color="error darken-3"
                      >
                        <v-icon small>fas fa-minus-circle</v-icon>
                      </v-btn>
                    </v-col>
                  </v-row>
                </draggable>
              </v-col>
              <v-col v-if="!category" cols="12" sm="12" md="6">
                <v-autocomplete
                  :disabled="isLoading"
                  :loading="isLoading"
                  v-model="curriculum_id"
                  name="curriculum_id"
                  label="Select Curriculum"
                  :items="curriculums"
                  :item-text="(item) => item.name"
                  :item-value="(item) => item.id"
                  @change="fetchCourse"
                  :menu-props="{ closeOnClick: true }"
                >
                  <template v-slot:selection="data">
                    {{ data.item.name }} &nbsp;
                    <span
                      :class="
                        data.item.is_draft
                          ? 'text-small'
                          : 'text-success text-small'
                      "
                      >({{ data.item.is_draft ? 'Draft' : 'Published' }})</span
                    >
                  </template>
                  <template v-slot:item="data">
                    <v-list-item-content>
                      <v-list-item-title>
                        {{ data.item.name }}
                        <span
                          :class="
                            data.item.is_draft
                              ? 'text-small'
                              : 'text-success text-small'
                          "
                          >({{
                            data.item.is_draft ? 'Draft' : 'Published'
                          }})</span
                        >
                      </v-list-item-title>
                      <v-list-item-subtitle
                        v-html="data.item.company_author.name"
                      ></v-list-item-subtitle>
                    </v-list-item-content>
                  </template>
                </v-autocomplete>
                <span
                  class="kt-font-danger validate-error"
                  v-show="errors.has('curriculum_id')"
                  >{{ errors.first('curriculum_id') }}</span
                >
              </v-col>
              <v-col v-if="!category" cols="12" sm="12" md="6">
                <v-autocomplete
                  :disabled="curriculum_id == null || isLoading"
                  v-model="module.category_id"
                  :items="courses"
                  :item-text="(item) => item.name"
                  :item-value="(item) => item.id"
                  :menu-props="{ closeOnClick: true }"
                  name="category_id"
                  label="Assign to Course"
                  :loading="isLoading"
                >
                  <template v-slot:selection="data">
                    {{ data.item.name }} &nbsp;
                    <span
                      :class="
                        data.item.is_draft
                          ? 'text-small'
                          : 'text-success text-small'
                      "
                      >({{ data.item.is_draft ? 'Draft' : 'Published' }})</span
                    >
                  </template>
                  <template v-slot:item="data">
                    {{ data.item.name }} &nbsp;
                    <span
                      :class="
                        data.item.is_draft
                          ? 'text-small'
                          : 'text-success text-small'
                      "
                      >({{ data.item.is_draft ? 'Draft' : 'Published' }})</span
                    >
                  </template>
                </v-autocomplete>
                <span
                  class="kt-font-danger validate-error"
                  v-show="errors.has('category_id')"
                  >{{ errors.first('category_id') }}</span
                >
              </v-col>
              <v-col cols="12" sm="12" md="12">
                <div class="form-group">
                  <label>Module Description</label>
                  <word-editor
                    :getMyData="module.description"
                    :secondValue.sync="module.description"
                    v-model="module.description"
                    name="description"
                    :disabled="isLoading"
                    :loading="isLoading"
                  />
                  <span
                    class="kt-font-danger validate-error"
                    v-show="errors.has('description')"
                    >{{ errors.first('description') }}</span
                  >
                </div>
              </v-col>
              <v-col cols="12" sm="12" md="12">
                <v-switch
                  v-model="module.is_draft"
                  :label="`Draft`"
                  :disabled="isLoading"
                  :loading="isLoading"
                ></v-switch>
              </v-col>
            </v-row>
          </v-container>
        </v-card-text>

        <v-card-actions>
          <v-spacer />
          <v-btn
            color="blue-grey darken-1"
            text
            :disabled="isLoading"
            :loading="isLoading"
            @click="reset"
            >Reset</v-btn
          >
          <v-btn
            color="blue-grey darken-1"
            text
            :disabled="isLoading"
            :loading="isLoading"
            @click="close"
            >Cancel</v-btn
          >
          <v-btn
            color="blue darken-1"
            text
            @click="save"
            :disabled="isLoading"
            :loading="isLoading"
            >Save</v-btn
          >
        </v-card-actions>
      </v-card>
      <!--    <show-files-->
      <!--      :toggle-files-dialog="toggleFilesDialog"-->
      <!--      :files-dialog="filesDialog"-->
      <!--    />-->
    </v-dialog>
    <helper-dialog
      :close-dialog="closeConfirmDialog"
      :show-dialog="showHelperDialog"
    >
      <ul>
        <li><strong>Module</strong> refers to the set of series.</li>
        <li>
          <strong>Module Description</strong> refers to the description of the
          module and would display to the module page.
        </li>
        <li><strong>Curriculum</strong> refers to the set of courses.</li>
        <li>
          <strong>Assign Course</strong> refers to the set of companies you want
          to show the curriculum.
        </li>
        <li>
          <strong>Draft</strong> refers to the status if you want your module to
          be displayed on your course.
        </li>
      </ul>
    </helper-dialog>
    <upload-file-dialog
      :type="['image']"
      :show-upload-dialog="showUploadDialog"
      :upload-dialog="toggleUploadDialog"
      :close-dialog="closeUploadDialog"
      v-on:showImage="getThumbnail"
    />
    <upload-file-dialog
      :type="'all'"
      :show-upload-dialog="showCallGuideDialog"
      :upload-dialog="toggleCallGuideDialog"
      :close-dialog="closeUploadDialog"
      v-on:showImage="getCallGuide"
    />
    <upload-file-dialog
      :name="'Insert URL'"
      :type="'all'"
      :show-upload-dialog="showLinksDialog"
      :upload-dialog="toggleCallGuideDialog"
      :close-dialog="closeUploadDialog"
      v-on:showImage="updateUrl"
      :key="currentIndex"
    />
  </div>
</template>

<script>
import WordEditor from '../../WordEditor.vue';
import ShowFiles from '@/components/client-filemanager/ShowFiles';
import HelperDialog from '@/components/dialogs/HelperDialog';
import UploadFileDialog from '@/components/dialogs/UploadFileDialog';
import { map as _map, filter as _filter, forEach as _forEach } from 'lodash-es';

export default {
  name: 'AddModuleDialog',
  components: {
    WordEditor,
    ShowFiles,
    HelperDialog,
    UploadFileDialog,
  },
  props: {
    category: Object,
    addModuleDialog: Boolean,
    closeModal: Function,
    toggleAddModuleDialog: Function,
  },
  data() {
    return {
      showLinksDialog: false,
      currentIndex: null,
      isLoading: false,
      fileUploadOrLink: false,
      noFileUpload: {
        display: false,
        message: null,
      },
      module: {
        id: null,
        name: null,
        category_id: null,
        link: null,
        image: [],
        description: null,
        call_guide_link: null,
        is_draft: false,
      },
      loading: {
        saving: false,
      },
      courses: [],
      categories: [],
      course: {},
      fetchingCategories: false,
      curriculum_id: null,
      filesDialog: false,
      showHelperDialog: false,
      showUploadDialog: false,
      showCallGuideDialog: false,
      thumbnail: null,
      thumbnailDisplay: null,
      links: [
        {
          name: null,
          url: null,
        },
      ],
    };
  },
  watch: {
    addModuleDialog(val) {
      val || this.close();
    },
  },
  computed: {
    width() {
      switch (this.$vuetify.breakpoint.name) {
        case 'xs':
          return '100%';
        case 'sm':
          return '100%';
        default:
          return '70%';
      }
    },
    courses() {
      return this.$store.state.courses.listCourses;
    },
    modules() {
      if (this.sortBy) {
        return _sortBy(this.$store.state.modules.modulesList);
      }
      return this.$store.state.modules.modulesList;
    },
    curriculums() {
      return this.$store.state.curriculums.curriculums;
    },
    curriculumsReady() {
      return this.$store.state.curriculums.isCurriculumsReady;
    },
  },
  created() {
    if (this.curriculums.length <= 0) {
      this.fetchCurriculums();
    }
  },
  methods: {
    updateUrl(item) {
      this.links[this.currentIndex].url = item.value;
    },
    addLink() {
      this.links.push({
        name: null,
        url: null,
      });
    },
    removeLink(index) {
      this.links.splice(index, 1);
    },
    toggleFilesDialog() {
      this.filesDialog = !this.filesDialog;
    },
    fetchCategories() {
      this.isLoading = true;
      this.axios
        .get('lms-manager/fetch/categoriesfromcourse', {
          params: {
            course_id: this.course_id,
          },
        })
        .then(({ data }) => {
          this.categories = data;
          this.isLoading = false;
        })
        .catch((error) => {
          console.log('something went wrong with fetching categories');
          this.isLoading = false;
        });
    },
    fetchCurriculums() {
      if (!this.curriculumsReady) {
        this.isLoading = true;
        this.setCurriculums();
        this.isLoading = false;
      }
    },
    fetchCourse() {
      this.isLoading = true;

      this.axios
        .get('/client/lms/fetchCurriculumCourses', {
          params: { curriculum_id: this.curriculum_id },
        })
        .then(({ data }) => {
          this.courses = data;
          this.$nextTick(() => {
            this.isLoading = false;
          });
        });
    },
    formData() {
      const formData = new FormData();
      formData.append('name', this.module.name);
      formData.append('description', this.module.description);
      formData.append('call_guide_link', this.module.call_guide_link);
      formData.append('is_draft', this.module.is_draft);

      let course_id = this.category
        ? this.category.id
        : this.module.category_id;
      formData.append('course_id', course_id);
      if (this.fileUploadOrLink) {
        formData.append('link', this.module.link);
      }

      formData.append('thumbnail', this.module.image);

      _forEach(_map(this.links), (link, index) => {
        if (link.url && link.name) {
          formData.append(`links[${index}][name]`, link.name);
          formData.append(`links[${index}][url]`, link.url);
          formData.append(`links[${index}][order]`, index);
        }
      });

      return formData;
    },

    save() {
      this.isLoading = true;
      this.$validator.validate('*').then((result) => {
        if (result) {
          this.$store
            .dispatch('modules/saveModule', this.formData())
            .then((response) => {
              this.$emit('addedModule', response.data);
              this.$notify('success', 'Module saved!');
              this.close();
              //
            })
            .catch((error) => {
              this.interactWithErrorBag(error, this.errors);
            })
            // eslint-disable-next-line no-return-assign
            .finally(() => (this.isLoading = false));
        } else {
          this.isLoading = false;
        }
      });
    },

    reset() {
      this.errors.clear();
      this.$validator.reset();
      this.module.name = null;
      this.curriculum_id = null;
      if (!this.category) this.module.category_id = null;
      this.module.description = null;
      this.module.is_draft = false;
      this.fileUploadOrLink = false;
      this.links = [
        {
          name: null,
          url: null,
        },
      ];
    },

    close() {
      this.reset();
      this.closeModal();
    },
    toggleHelperDialog() {
      this.showHelperDialog = true;
    },
    closeConfirmDialog() {
      this.showHelperDialog = false;
    },
    toggleUploadDialog() {
      this.showUploadDialog = true;
    },
    toggleCallGuideDialog() {
      this.showCallGuideDialog = true;
    },
    toggleLinkDialog(index) {
      this.currentIndex = index;
      this.showLinksDialog = true;
    },
    closeUploadDialog() {
      this.showUploadDialog = false;
      this.showCallGuideDialog = false;
      this.showLinksDialog = false;
    },
    getThumbnail(item) {
      this.module.image = item.value;
      if (item.type == 'upload') {
        this.thumbnailDisplay = URL.createObjectURL(this.module.image);
      } else {
        this.thumbnailDisplay = this.module.image;
      }
    },
    getCallGuide(item) {
      this.module.call_guide_link = item.value;
    },
  },
};
</script>

<style scoped>
@media only screen and (max-width: 600px) {
  .headline {
    font-size: 10px !important;
  }
}
</style>
